
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import SectionHeader from '../../components/ui/section-header';
import ProfileViewModal from '../../components/module/my-team/modal/profile-view-modal';
import { departmentTypesForApi } from '../../api/user/user-api';
import { useDepartmentUserAccess } from '../../hooks/access/use-department-user-access';
import { useMyTeamDataState } from '../../hooks/data-state/use-my-team-data-state';
import { setPrimaryModalMyTeam, setSearchValueMyTeam, setUserIdMyTeam } from '../../features/my-team/my-team-slice';
import TableEmpty from '../../components/ui/table-empty';
import TableLoading from '../../components/ui/table-loading';
import TableError from '../../components/ui/table-error';
import MyTeamTHead from '../../components/module/my-team/common/my-team-thead';

interface Props {
    departmentType: departmentTypesForApi;
}

export default function MyTeam({ departmentType }: Props) {
    const dispatch = useDispatch();

    const [statusFilter, setStatusFilter] = useState<'active' | 'inactive'>('active');

    const {
        filteredUsers: gmsUsers,
        getDepartmentUserById,
        isLoadingDepartmentUsers,
        isErrorDepartmentUsers,
    } = useDepartmentUserAccess({ departmentType });

    const {
        isShowViewProfilePrimaryMyTeam,
        searchValueMyTeam,
        userIdMyTeam,
    } = useMyTeamDataState();

    const handleViewUser = (userId: number) => {
        dispatch(setPrimaryModalMyTeam('viewProfile'));
        dispatch(setUserIdMyTeam(userId));
    };

    // Filter users based on selected status
    const displayedUsers = gmsUsers.filter(user =>
        statusFilter === 'active'
            ? user.is_active === true
            : user.is_active === false
    );

    const inputStyle = 'w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-xs text-gray-700 outline-none transition focus:border-[#186cb2] focus:ring-2 focus:ring-[#186cb2]/10 disabled:cursor-not-allowed disabled:bg-gray-100';

    return (
        <div className="flex h-full min-h-0 w-full flex-col rounded-xl border border-gray-200 bg-[#f8fafc]">
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                <SectionHeader
                    title="My Team"
                    subtitle="View your team members and monitor their information and status."
                    icon={
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                            <path d="M16 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                            <circle cx="10" cy="7" r="4" />
                            <path d="M20 21v-2a4 4 0 0 0-3-3.87" />
                            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                        </svg>
                    }
                />

                <div className="mx-auto flex min-h-0 w-full flex-1 flex-col gap-2">
                    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-white shadow-sm">

                        {/* Status Filter */}
                        <div className="flex shrink-0 flex-col gap-3 border-b border-gray-100 p-2 sm:p-3">
                            <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-1 rounded-lg bg-gray-100 p-1">
                                    <button
                                        type="button"
                                        onClick={() => setStatusFilter('active')}
                                        className={`inline-flex items-center justify-center gap-1.5 rounded-md px-4 py-2 text-[11px] font-semibold transition ${
                                            statusFilter === 'active'
                                                ? 'bg-white text-green-600 shadow-sm'
                                                : 'text-gray-500 hover:bg-white/60 hover:text-gray-700'
                                        }`}
                                    >
                                        <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                                        Active
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => setStatusFilter('inactive')}
                                        className={`inline-flex items-center justify-center gap-1.5 rounded-md px-4 py-2 text-[11px] font-semibold transition ${
                                            statusFilter === 'inactive'
                                                ? 'bg-white text-red-500 shadow-sm'
                                                : 'text-gray-500 hover:bg-white/60 hover:text-gray-700'
                                        }`}
                                    >
                                        <span className="h-1.5 w-1.5 rounded-full bg-red-400" />
                                        Inactive
                                    </button>
                                </div>
                            </div>

                            {/* Search */}
                            <div className="relative w-full sm:max-w-md">
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400"
                                >
                                    <circle cx="11" cy="11" r="7" />
                                    <path strokeLinecap="round" d="m16.5 16.5 4 4" />
                                </svg>

                                <input
                                    type="text"
                                    value={searchValueMyTeam}
                                    onChange={(event) => dispatch(setSearchValueMyTeam(event.target.value))}
                                    placeholder="Search name, location, email, number, or status..."
                                    className={`${inputStyle} min-w-0 pl-9`}
                                />
                            </div>
                        </div>

                        {/* Users Table */}
                        <div className="min-h-0 flex-1 overflow-auto border-t bg-white shadow-sm">
                            <table className="min-w-[900px] w-full border-collapse">
                                <MyTeamTHead />

                                <tbody>
                                    {isLoadingDepartmentUsers ? (
                                        <TableLoading />
                                    ) : isErrorDepartmentUsers ? (
                                        <TableError />
                                    ) : displayedUsers.length === 0 && searchValueMyTeam.trim().length !== 0 ? (
                                        <tr>
                                            <td colSpan={7} className="px-3 py-12 text-center">
                                                <div className="flex flex-col items-center justify-center">
                                                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#eef6fc] text-[#186cb2]">
                                                        <svg
                                                            className="h-4 w-4"
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            strokeWidth="1.8"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        >
                                                            <circle cx="11" cy="11" r="7" />
                                                            <path d="m20 20-4-4" />
                                                        </svg>
                                                    </div>

                                                    <p className="mt-2 text-xs font-medium text-gray-600">
                                                        No users found
                                                    </p>

                                                    <p className="mt-1 text-[10px] text-gray-400">
                                                        Try adjusting your search.
                                                    </p>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : displayedUsers.length === 0 ? (
                                        <TableEmpty />
                                    ) : (
                                        displayedUsers.map((user, index) => (
                                            <tr
                                                key={user.user_id ?? index}
                                                className="border-b border-gray-100 transition hover:bg-[#eef6fc]/50"
                                            >
                                                <td className="px-3 py-3 text-xs font-medium text-gray-700">
                                                    {user.name ?? '-'}
                                                </td>

                                                <td className="px-3 py-3 text-xs text-gray-600">
                                                    {user.location_name ?? '-'}
                                                </td>

                                                <td className="px-3 py-3 text-xs text-gray-600">
                                                    {user.email ?? '-'}
                                                </td>

                                                <td className="px-3 py-3 text-xs text-gray-600">
                                                    {user.position ?? '-'}
                                                </td>

                                                <td className="px-3 py-3">
                                                    <span
                                                        className={`inline-flex rounded-full px-2 py-1 text-[9px] font-semibold ${
                                                            user.is_active
                                                                ? 'bg-green-50 text-green-600'
                                                                : 'bg-gray-100 text-gray-500'
                                                        }`}
                                                    >
                                                        {user.is_active ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>

                                                <td className="px-3 py-3 text-center">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleViewUser(user.user_id)}
                                                        className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[#186cb2]/20 bg-white px-2.5 text-[11px] font-semibold text-[#186cb2] transition hover:border-[#186cb2]/40 hover:bg-[#186cb2]/5 active:scale-95"
                                                    >
                                                        <svg
                                                            className="h-3.5 w-3.5"
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            strokeWidth="1.8"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        >
                                                            <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" />
                                                            <circle cx="12" cy="12" r="2.5" />
                                                        </svg>
                                                        View
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* Profile Modal */}
            <ProfileViewModal
                isOpen={isShowViewProfilePrimaryMyTeam}
                onClose={() => {
                    dispatch(setPrimaryModalMyTeam(null));
                    dispatch(setUserIdMyTeam(null));
                }}
                userId={userIdMyTeam}
                getDepartmentUserById={getDepartmentUserById}
            />
        </div>
    );
}
